import { Clock, MapPin, User } from "lucide-react";

// getTimeTable's real populated shape is still unconfirmed (every real
// request so far has hit "No Time Table Records Found"), so every field is
// read defensively against the handful of plausible names a period record
// could use, instead of assuming one shape.
const pick = (row, keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

const RECOGNIZED_KEYS = [
  "period",
  "period_no",
  "period_number",
  "slot",
  "subject",
  "subject_name",
  "time",
  "time_slot",
  "start_time",
  "end_time",
  "teacher",
  "teacher_name",
  "room",
  "room_no",
  "venue",
  "id",
];

const humanizeKey = (key) => key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * One row of the periods table, rendered as a clean card instead of a table
 * row. Shows recognized fields (period/subject/time/teacher/room) nicely
 * laid out when present; any field the API sends back that we don't
 * recognize is still shown as a plain label/value line underneath, so a
 * real response never silently loses data even if its shape doesn't match
 * what's expected here.
 */
const TimetablePeriodCard = ({ row, index }) => {
  const periodLabel = pick(row, ["period", "period_no", "period_number", "slot"]);
  const subject = pick(row, ["subject", "subject_name"]);
  const timeRange =
    pick(row, ["time", "time_slot"]) ||
    [pick(row, ["start_time"]), pick(row, ["end_time"])].filter(Boolean).join(" - ") ||
    null;
  const teacher = pick(row, ["teacher", "teacher_name"]);
  const room = pick(row, ["room", "room_no", "venue"]);

  const extraEntries = Object.entries(row || {}).filter(
    ([key, value]) => !RECOGNIZED_KEYS.includes(key) && value !== undefined && value !== null && value !== ""
  );

  return (
    <div className="sp-card timetable-period-card" data-tone="primary">
      <div className="timetable-period-card__head">
        <span className="sp-icon-tile timetable-period-card__badge">{periodLabel ?? index + 1}</span>
        <div className="timetable-period-card__title-group">
          <h5 className="timetable-period-card__subject">{subject || "Period " + (periodLabel ?? index + 1)}</h5>
          {timeRange && (
            <span className="timetable-period-card__meta-item">
              <Clock size={14} /> {timeRange}
            </span>
          )}
        </div>
      </div>

      {(teacher || room) && (
        <div className="timetable-period-card__meta">
          {teacher && (
            <span className="timetable-period-card__meta-item">
              <User size={14} /> {teacher}
            </span>
          )}
          {room && (
            <span className="timetable-period-card__meta-item">
              <MapPin size={14} /> {room}
            </span>
          )}
        </div>
      )}

      {extraEntries.length > 0 && (
        <dl className="timetable-period-card__extra">
          {extraEntries.map(([key, value]) => (
            <div key={key} className="timetable-period-card__extra-row">
              <dt>{humanizeKey(key)}</dt>
              <dd>{String(value)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

export default TimetablePeriodCard;
